"use client"

import * as React from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Archive, ChevronRight, Clock, Plus, User } from "lucide-react"

import { api, type Skill, type Workflow } from "@/lib/api"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { WorkflowBuilder } from "@/components/workflow-builder"
import { workflowTags, triggerLabel } from "@/lib/workflow-summary"

export const scheduleLabel = (wf: Workflow) => triggerLabel(wf.spec.schedule)

/**
 * The workflow index.
 *
 * A row is a link, not a toggle that expands a panel underneath: the detail
 * view is its own route, so it deep-links and the back button works.
 */
export function WorkflowsList({ skills }: { skills: Skill[] }) {
  const router = useRouter()
  const [creating, setCreating] = React.useState(false)
  const [list, setList] = React.useState<Workflow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [showArchived, setShowArchived] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Fetch inside the effect with a cancel flag rather than calling a
  // setState-first loader: toggling "show archived" quickly could otherwise
  // let a slower earlier response land last and win.
  const [reloadKey, setReloadKey] = React.useState(0)
  React.useEffect(() => {
    let cancelled = false
    void (async () => {
      const res = await api.workflows(showArchived)
      if (cancelled) return
      setList(res.data?.workflows ?? [])
      setError(res.error)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [showArchived, reloadKey])

  const load = React.useCallback(() => setReloadKey((k) => k + 1), [])

  if (creating) {
    return (
      <WorkflowBuilder
        skills={skills}
        onCancel={() => setCreating(false)}
        onSaved={() => {
          setCreating(false)
          load()
        }}
      />
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Workflows</h2>
          <p className="text-sm text-muted-foreground">
            Orchestration built from a prompt. A plan may only compose existing skills.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)} className="gap-1.5">
            <Archive className="size-3.5" />
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          <Button className="gap-2" onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            New workflow
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/40">
          <CardContent className="py-3">
            <p className="font-mono text-xs text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-[74px] w-full" />
          <Skeleton className="h-[74px] w-full" />
          <Skeleton className="h-[74px] w-full" />
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-lg border border-dashed py-16 text-center">
          <p className="text-sm font-medium">No workflows yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create one and describe what it should do.
          </p>
          <Button className="mt-4 gap-2" onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            New workflow
          </Button>
        </div>
      ) : (
        <div className="divide-y rounded-lg border">
          {list.map((wf) => (
            <Link
              key={wf.id}
              href={`/workflows/${wf.id}`}
              // Prefetch on hover so the detail page feels immediate.
              onMouseEnter={() => router.prefetch(`/workflows/${wf.id}`)}
              className="group flex items-center justify-between gap-4 px-4 py-3.5 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-muted/40"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">{wf.name}</p>
                  {wf.archived && (
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      archived
                    </Badge>
                  )}
                </div>
                {wf.description && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{wf.description}</p>
                )}
                {/* Derived from the plan, so they cannot disagree with it. */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {workflowTags(wf).map((t) => (
                    <span
                      key={t.label}
                      title={t.title}
                      className={
                        "rounded-full border px-2 py-0.5 text-[11px] leading-none " +
                        (t.tone === "accent"
                          ? "border-primary/30 bg-primary/10 text-primary"
                          : t.tone === "warn"
                            ? "border-destructive/40 bg-destructive/10 text-destructive"
                            : "border-border bg-muted/60 text-muted-foreground")
                      }
                    >
                      {t.label}
                    </span>
                  ))}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="size-3" />
                    {wf.createdBy ?? "unknown"}
                  </span>
                  <span>created {new Date(wf.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant={wf.enabled ? "default" : "secondary"}>
                  {wf.enabled ? "on" : "off"}
                </Badge>
                <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
